/**
 * ProductTile custom element — AAS-213
 *
 * Handles:
 *  - Image carousel (prev/next arrows, progress bar, swipe, adaptive contrast)
 *  - Size button click → select size (visual only)
 *  - Cart "+" button → submits the embedded <product-form> with the resolved
 *    variant id when colour and size (where applicable) are both selected.
 *    Opens the product quick-view modal when either required option is missing.
 *  - Compare / wishlist placeholder buttons
 *
 * State is managed entirely via CSS classes and data-* attributes.
 * Variant resolution uses the JSON variants list embedded inside the tile.
 */

if (!customElements.get('product-tile')) {
  class ProductTile extends HTMLElement {
    constructor() {
      super();
      this._currentIndex = 0;
      this._totalSlides = 0;
      this._selectedColor = null;
      this._selectedSize = null;
      this._isCarouselHovered = false;
      this._indexBeforeHover = null;
      this._carouselMedia = null;
    }

    connectedCallback() {
      this._initElements();
      this._initCarousel();
      this._initSizes();
      this._initCartButton();
      this._initActionButtons();
    }

    disconnectedCallback() {
      this._carouselMedia?.disconnect();
      this._carouselMedia = null;
    }

    // -------------------------------------------------------------------------
    // Element queries
    // -------------------------------------------------------------------------
    _initElements() {
      this.carousel     = this.querySelector('.product-tile__carousel');
      this.track        = this.querySelector('[data-carousel-track]');
      this.slides       = this.track ? Array.from(this.track.children) : [];
      this.btnPrev      = this.querySelector('[data-carousel-prev]');
      this.btnNext      = this.querySelector('[data-carousel-next]');
      this.progressBar  = this.querySelector('[data-carousel-progress]');
      this.progressRoot = this.progressBar ? this.progressBar.closest('[role="progressbar"]') : null;
      this.sizeBtns     = Array.from(this.querySelectorAll('.product-tile-size-btn'));
      this.sizeToggle   = this.querySelector('[data-size-toggle]');
      this.sizeList     = this.querySelector('.product-tile-sizes');
      this.primaryImage = this.querySelector('.product-tile__img--primary')
        || (this.track ? this.track.querySelector('img') : null)
        || this.querySelector('.product-tile__image-link img');
      this.imageLink    = this.querySelector('.product-tile__image-link');
      this._totalSlides = this.slides.length;

      this.cartForm         = this.querySelector('product-form form');
      this.cartFormHiddenId = this.querySelector('.product-variant-id');
      this.cartBtn          = this.querySelector('.product-tile__cart-btn');
      this.colorOptionIdx   = parseInt(this.dataset.colorOptionIndex, 10);
      this.sizeOptionIdx    = parseInt(this.dataset.sizeOptionIndex, 10);
      this.colorRequired    = this.dataset.colorRequired === 'true';
      this.sizeRequired     = this.dataset.sizeRequired === 'true';

      const variantsScript = this.querySelector('[data-variants-json]');
      try {
        this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
      } catch (_e) {
        this.variants = [];
      }

      this._syncSelectionFromDom();
    }

    /* Shared with the other tile — see assets/tile-shared.js. */
    _syncSelectionFromDom() {
      window.ArmadaTile?.syncSelectionFromDom(this);
    }

    _initCartButton() {
      window.ArmadaTile?.initCartButton(this);
    }

    _findMatchingVariants() {
      return window.ArmadaTile?.findMatchingVariants(this) || [];
    }

    // -------------------------------------------------------------------------
    // Carousel
    // -------------------------------------------------------------------------
    _initCarousel() {
      if (this.dataset.mediaMode === 'hover_swap') return;
      if (!this.track || this._totalSlides < 2) return;

      this.btnPrev?.addEventListener('click', () => this._navigate(-1));
      this.btnNext?.addEventListener('click', () => this._navigate(1));

      // Desktop hover: show "next" image while hovered
      this.carousel?.addEventListener('mouseenter', () => this._setCarouselHovered(true));
      this.carousel?.addEventListener('mouseleave', () => this._setCarouselHovered(false));

      this._carouselMedia = new ArmadaCarouselMediaEnhancer({
        carousel: this.carousel,
        slides: this.slides,
        btnPrev: this.btnPrev,
        btnNext: this.btnNext,
        getCurrentIndex: () => this._currentIndex,
        navigate: (delta) => this._navigate(delta),
      });
      this._carouselMedia.connect();

      this._updateCarousel();
    }

    _getHoverIndex(baseIndex) {
      if (typeof baseIndex !== 'number') return baseIndex;
      if (baseIndex < 0 || baseIndex >= this._totalSlides) return baseIndex;
      // Keep behaviour consistent with arrows: no wrap past last slide.
      if (baseIndex >= this._totalSlides - 1) return baseIndex;
      return baseIndex + 1;
    }

    _setCarouselHovered(isHovering) {
      if (!this.track || this._totalSlides < 2) return;
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      if (isHovering === this._isCarouselHovered) return;
      this._isCarouselHovered = isHovering;

      if (this._isCarouselHovered) {
        this._indexBeforeHover = this._currentIndex;
        this._currentIndex = this._getHoverIndex(this._indexBeforeHover);
        this._updateCarousel();
        return;
      }

      if (typeof this._indexBeforeHover === 'number') {
        this._currentIndex = this._indexBeforeHover;
      }
      this._indexBeforeHover = null;
      this._updateCarousel();
    }

    _navigate(direction) {
      // If the carousel is hovered, arrows should move the "base" index,
      // and the displayed slide should stay as base+1 (hover state).
      if (this._isCarouselHovered && typeof this._indexBeforeHover === 'number') {
        const baseNext = this._indexBeforeHover + direction;
        if (baseNext < 0 || baseNext >= this._totalSlides) return;
        this._indexBeforeHover = baseNext;
        this._currentIndex = this._getHoverIndex(baseNext);
        this._updateCarousel();
        return;
      }
      const next = this._currentIndex + direction;
      if (next < 0 || next >= this._totalSlides) return;
      this._currentIndex = next;
      this._updateCarousel();
    }

    _updateCarousel() {
      if (!this.track) return;

      this.track.style.transform = `translateX(-${this._currentIndex * 100}%)`;

      // Progress bar
      if (this.progressBar) {
        const segmentPct = 100 / this._totalSlides;
        const translatePct = segmentPct * this._currentIndex;
        this.progressBar.style.width = `${segmentPct.toFixed(2)}%`;
        this.progressBar.style.left = `${translatePct.toFixed(2)}%`;
      }
      if (this.progressRoot) {
        this.progressRoot.setAttribute('aria-valuenow', this._currentIndex);
      }

      // Arrow disabled states
      if (this.btnPrev) {
        this.btnPrev.disabled = this._currentIndex === 0;
      }
      if (this.btnNext) {
        this.btnNext.disabled = this._currentIndex === this._totalSlides - 1;
      }

      // Update tabindex on slide links so only active slide is reachable
      this.slides.forEach((slide, i) => {
        const link = slide.querySelector('a');
        if (link) link.tabIndex = i === this._currentIndex ? 0 : -1;
      });

      this._carouselMedia?.scheduleArrowContrastUpdate();
    }

    // -------------------------------------------------------------------------
    // Size interactions
    // -------------------------------------------------------------------------
    _initSizes() {
      this.sizeBtns.forEach((btn) => {
        btn.addEventListener('click', () => this._handleSizeClick(btn));
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._handleSizeClick(btn);
          }
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            this._focusNextSize(btn, 1);
          }
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            this._focusNextSize(btn, -1);
          }
        });
      });

      // Mobile size toggle
      this.sizeToggle?.addEventListener('click', () => {
        const isExpanded = this.sizeToggle.getAttribute('aria-expanded') === 'true';
        this.sizeToggle.setAttribute('aria-expanded', !isExpanded);
        this.sizeList?.classList.toggle('is-open', !isExpanded);
      });
    }

    _handleSizeClick(btn) {
      this._setActiveSize(btn);
      this._selectedSize = btn.dataset.sizeValue;
      this._updatePrice();
    }

    /* Price resolution + repaint live in assets/tile-shared.js, shared with
       <product-tile-v2>; both expose the same variants/selection contract.

       Size only, unlike v2: this tile's colour swatches are a separate element
       (<card-product-swatch> in theme.js) that swaps the image and the product
       link without touching _selectedColor, so a colour change does not move
       this tile's variant at all — the ATC form does not follow it either.
       Wiring that up is a behaviour change beyond pricing. */
    _updatePrice() {
      window.ArmadaTile?.updatePrice(this);
    }

    _setActiveSize(btn) {
      this.sizeBtns.forEach((b) => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-checked', 'false');
      });
      btn.classList.add('is-selected');
      btn.setAttribute('aria-checked', 'true');
    }

    _focusNextSize(current, direction) {
      const enabled = this.sizeBtns.filter((b) => !b.disabled);
      const idx  = enabled.indexOf(current);
      const next = enabled[idx + direction];
      if (next) next.focus();
    }

    _handleCartClick(event) {
      if (!this.cartForm) return;

      // Single-variant product (no variants JSON embedded): the hidden id input
      // already holds the only variant id — let the form submit as-is.
      if (!this.variants.length) return;

      // Re-read pre-selected size in case state drifted after init.
      this._syncSelectionFromDom();

      // Open quick-view when either selector is present on the card and its
      // value hasn't been picked. Colour defaults from data-default-color;
      // size has no default.
      const hasUnselectedColor = this.colorOptionIdx >= 0 && this.colorRequired && !this._selectedColor;
      const hasUnselectedSize  = this.sizeOptionIdx  >= 0 && this.sizeRequired  && !this._selectedSize;
      if (hasUnselectedColor || hasUnselectedSize) {
        event.preventDefault();
        this._openQuickView(this.dataset.productUrl);
        return;
      }

      const matches = this._findMatchingVariants();
      if (matches.length === 0) {
        event.preventDefault();
        this._openQuickView(this.dataset.productUrl);
        return;
      }

      // For single-value options not exposed/selected on the card we fall back
      // to the first available variant that matches the user's actual choices.
      if (this.cartFormHiddenId) {
        this.cartFormHiddenId.value = matches[0].id;
      }
    }

    // -------------------------------------------------------------------------
    // Quick-view modal
    // -------------------------------------------------------------------------
    _openQuickView(productUrl) {
      if (!productUrl) return;
      // Find the hidden quick-view trigger on this tile and dispatch a click on it
      const qv = this.querySelector('.product-tile__quick-view-trigger') || this.querySelector('quick-view');
      if (qv) {
        const prev = qv.dataset.productUrl;
        qv.dataset.productUrl = productUrl;
        qv.click();
        qv.dataset.productUrl = prev;
      } else {
        // Fallback: dispatch a global event for any listener
        document.dispatchEvent(
          new CustomEvent('product-tile:open-quick-view', {
            bubbles: true,
            detail: { productUrl, tile: this }
          })
        );
      }
    }

    // Action buttons (wishlist).
    // Compare is wired globally in assets/armada-compare.js via event delegation
    // on [data-compare-btn], so we don't bind a per-tile click handler here.
    // -------------------------------------------------------------------------
    _initActionButtons() {
      const wishlistBtn = this.querySelector('[data-wishlist-btn]');

      wishlistBtn?.addEventListener('click', () => {
        const isActive = wishlistBtn.classList.toggle('is-active');
        wishlistBtn.setAttribute(
          'aria-label',
          isActive
            ? (wishlistBtn.dataset.removeLabel || 'Remove from wishlist')
            : (wishlistBtn.dataset.addLabel   || 'Add to wishlist')
        );
        document.dispatchEvent(
          new CustomEvent('product-tile:wishlist', {
            bubbles: true,
            detail: { productId: wishlistBtn.dataset.productId, active: isActive, tile: this }
          })
        );
      });
    }
  }

  customElements.define('product-tile', ProductTile);
}
